"use client";

import { DialogContent, DialogOverlay, DialogTrigger } from "@/components/rac/dialog";
import { Button } from "@/components/rac/button";
import { Input } from "@/components/rac/input";
import { MapItemSUP } from "@/types/search";
import { s3Url } from "@/utils/s3-prefix";
import { Search } from "lucide-react";
import Image from "next/image";
import { useState } from "react";

interface PropertySearchModalProps {
	trigger: React.ReactNode;
	onSelectProperty: (property: MapItemSUP) => void;
	excludePropertyIds?: string[];
}

// This will be replaced with actual search API call
const mockSearchProperties = async (query: string): Promise<MapItemSUP[]> => {
	// TODO: Implement actual search API call

	// Validate input
	if (!query || typeof query !== "string") {
		throw new Error("Invalid search query: Query must be a non-empty string");
	}

	const trimmedQuery = query.trim();
	if (trimmedQuery.length === 0) {
		throw new Error("Invalid search query: Query cannot be empty");
	}

	if (trimmedQuery.length < 2) {
		throw new Error("Invalid search query: Query must be at least 2 characters long");
	}

	// Simulate network delay and potential failures
	try {
		// Simulate API call delay
		await new Promise(resolve => setTimeout(resolve, 500));

		// TODO: Replace with actual API call
		// const response = await fetch('/api/search-properties', {
		//   method: 'POST',
		//   headers: { 'Content-Type': 'application/json' },
		//   body: JSON.stringify({ query: trimmedQuery })
		// });
		//
		// if (!response.ok) {
		//   throw new Error(`Search failed: ${response.statusText}`);
		// }
		//
		// const data = await response.json();
		// return data.properties || [];

		// For now, return empty array (graceful degradation)
		return [];
	} catch (error) {
		// Re-throw with context
		if (error instanceof Error) {
			throw new Error(`Property search failed: ${error.message}`);
		}
		throw new Error("Property search failed: Unknown error occurred");
	}
};

export function PropertySearchModal({ trigger, onSelectProperty, excludePropertyIds = [] }: PropertySearchModalProps) {
	const [searchQuery, setSearchQuery] = useState("");
	const [searchResults, setSearchResults] = useState<MapItemSUP[]>([]);
	const [isSearching, setIsSearching] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const handleSearch = async (query: string) => {
		// Reset error state
		setErrorMessage(null);

		// Validate query before searching
		if (!query.trim()) {
			setSearchResults([]);
			setErrorMessage("Please enter a search term");
			return;
		}

		if (query.trim().length < 2) {
			setErrorMessage("Search query must be at least 2 characters long");
			return;
		}

		setIsSearching(true);
		setSearchResults([]);

		try {
			const results = await mockSearchProperties(query);

			// Handle empty results
			if (!results || results.length === 0) {
				setSearchResults([]);
				// Don't set error here - "no results" is handled in UI separately
				return;
			}

			// Filter out already added properties
			const filteredResults = results.filter(r => !excludePropertyIds.includes(r._id));

			// Check if all results were filtered out
			if (results.length > 0 && filteredResults.length === 0) {
				setSearchResults([]);
				setErrorMessage("All matching properties are already in comparison");
				return;
			}

			setSearchResults(filteredResults);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Error searching properties:", error);
			setSearchResults([]);

			// Provide user-friendly error message
			if (error instanceof Error) {
				if (error.message.includes("Invalid search query")) {
					setErrorMessage(error.message.replace("Invalid search query: ", ""));
				} else if (error.message.includes("Network")) {
					setErrorMessage("Unable to connect to server. Please check your internet connection and try again.");
				} else if (error.message.includes("timeout")) {
					setErrorMessage("Search is taking too long. Please try again.");
				} else {
					setErrorMessage("Something went wrong while searching. Please try again.");
				}
			} else {
				setErrorMessage("An unexpected error occurred. Please try again.");
			}
		} finally {
			setIsSearching(false);
		}
	};

	const handleSelectProperty = (property: MapItemSUP) => {
		onSelectProperty(property);
		setIsOpen(false);
		setSearchQuery("");
		setSearchResults([]);
		setErrorMessage(null);
	};

	return (
		<DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
			{trigger}
			<DialogOverlay isDismissable>
				<DialogContent closeButton={true} className="max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
					{/* Header */}
					<div className="flex items-center justify-between pb-4 border-b border-gray-200">
						<h2 className="text-xl font-semibold text-gray-900">Select Property to Compare</h2>
					</div>

					{/* Search */}
					<div className="p-6 border-b border-gray-200">
						<div className="flex gap-2">
							<Input
								value={searchQuery}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
								className="flex-1"
								placeholder="Search by project name, locality, or address..."
								onKeyDown={(e: React.KeyboardEvent) => {
									if (e.key === "Enter") {
										handleSearch(searchQuery);
									}
								}}
							/>
							<Button onPress={() => handleSearch(searchQuery)} size="sm" className="flex items-center gap-2">
								<Search className="w-4 h-4" />
								<span>Search</span>
							</Button>
						</div>
						<p className="text-sm text-gray-500 mt-2">Enter project name, locality, or address to find properties</p>
					</div>

					{/* Results */}
					<div className="flex-1 overflow-y-auto p-6">
						{/* Error State */}
						{errorMessage && !isSearching && (
							<div className="flex items-center justify-center py-12">
								<div className="text-center max-w-md">
									<div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
										<svg
											className="w-6 h-6 text-red-600"
											fill="none"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											viewBox="0 0 24 24"
											stroke="currentColor"
										>
											<path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
										</svg>
									</div>
									<p className="text-red-600 font-medium mb-2">Search Error</p>
									<p className="text-gray-600 text-sm">{errorMessage}</p>
									<Button onPress={() => handleSearch(searchQuery)} variant="outline" size="sm" className="mt-4">
										Try Again
									</Button>
								</div>
							</div>
						)}

						{/* Loading State */}
						{isSearching && (
							<div className="flex items-center justify-center py-12">
								<div className="text-center">
									<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
									<p className="text-gray-600">Searching properties...</p>
								</div>
							</div>
						)}

						{/* No Results State */}
						{!isSearching && !errorMessage && searchQuery && searchResults.length === 0 && (
							<div className="flex items-center justify-center py-12">
								<div className="text-center">
									<Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
									<p className="text-gray-600 font-medium">No properties found</p>
									<p className="text-gray-500 text-sm mt-1">Try a different search term</p>
								</div>
							</div>
						)}

						{/* Empty State */}
						{!isSearching && !errorMessage && !searchQuery && (
							<div className="flex items-center justify-center py-12">
								<div className="text-center">
									<Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
									<p className="text-gray-600 font-medium">Start your search</p>
									<p className="text-gray-500 text-sm mt-1">Enter a project name or locality to find properties</p>
								</div>
							</div>
						)}

						{searchResults.length > 0 && (
							<div className="space-y-3">
								{searchResults.map(property => (
									<button
										key={property._id}
										onClick={() => handleSelectProperty(property)}
										className="w-full flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary-50 transition-colors text-left"
									>
										{property.cover && (
											<div className="relative w-20 h-20 flex-shrink-0 rounded overflow-hidden">
												<Image
													src={s3Url(property.cover)}
													alt={property.projectName || "Property"}
													fill
													className="object-cover"
												/>
											</div>
										)}
										<div className="flex-1 min-w-0">
											<h3 className="font-semibold text-gray-900 truncate">{property.projectName}</h3>
											<p className="text-sm text-gray-600 truncate">{property.localityName || property.locality}</p>
											{property.prices?.[0] && (
												<p className="text-sm font-medium text-primary mt-1">
													₹
													{property.prices[0] >= 10000000
														? `${(property.prices[0] / 10000000).toFixed(2)} Cr`
														: `${(property.prices[0] / 100000).toFixed(2)} L`}
												</p>
											)}
										</div>
										<div className="flex-shrink-0">
											<div className="text-primary text-sm font-medium">Select</div>
										</div>
									</button>
								))}
							</div>
						)}
					</div>
				</DialogContent>
			</DialogOverlay>
		</DialogTrigger>
	);
}
